import { useRef, useState } from "react";

import GjsEditor, {
  AssetsProvider,
  Canvas,
  ModalProvider,
  WithEditor,
  useEditor,
} from "@grapesjs/react";
import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import grapesjs, { Editor, EditorConfig, ProjectData } from "grapesjs";
import { renderToStaticMarkup } from "react-dom/server";
import "../builder/grapes.min.css";

import { BuilderHeader } from "./BuilderHeader";
import { BuilderLeftSidebar } from "./BuilderLeftSidebar";
import { BuilderRightSidebar } from "./BuilderRightSidebar";
import { CustomAssetManager } from "./asset-manager/CustomAssetManager";
import { createCanvasCss } from "./canvasCSS";
import { CustomModal } from "./custom-modal/CustomModal";
import { BaseHLComponentPlugin } from "./plugins/BaseHLComponentPlugin";
import { CoreAdjustmentsPlugin } from "./plugins/CoreAdjustmentsPlugin";
import { CoreComponentsPlugin } from "./plugins/CoreComponentsPlugin";
import { HLCollectionCardPlugin } from "./plugins/HLCollectionCardPlugin";

type CurationBuilderProps = {
  initialProjectData?: ProjectData | null;
  onSetHtml: (html: string) => void;
  onSave: (data: { css: string; html: string; data: ProjectData }) => void;
  onShowPreview: () => void;
};

export const CurationBuilder = (props: CurationBuilderProps) => {
  const { initialProjectData, onSave, onShowPreview } = props;
  const theme = useMantineTheme();
  const editorRef = useRef<Editor | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const { canvasOnlyStyles, exportedStyles } = createCanvasCss(theme);

  const devices = [
    {
      name: "Desktop",
      width: "",
    },
    {
      name: "Tablet",
      width: theme.breakpoints.md + "px",
      widthMedia: theme.breakpoints.lg - 1 + "px",
    },
    {
      name: "Mobile",
      width: 456 + "px",
      widthMedia: theme.breakpoints.md - 1 + "px",
    },
  ];

  // Assets with removed duplicates
  const assets = [
    "https://highlight.xyz/static/images/share-cards/homePage.png",
    "https://highlight.xyz/static/images/art/Image%2001.png",
    ...(initialProjectData?.assets?.map(
      (asset: { src: string }) => asset.src
    ) ?? []),
  ].filter((value, index, self) => self.indexOf(value) === index);

  const projectData = {
    pages: [
      {
        name: "Curation page",
        // initial HTML
        component: renderToStaticMarkup(<div data-gjs-type="hl-wrapper"></div>),
      },
    ],
    ...(initialProjectData ?? {}),
    assets,
  };

  const gjsOptions: EditorConfig = {
    mediaCondition: "max-width",
    deviceManager: {
      devices,
    },
    // Styles that get exported to the final HTML
    protectedCss: exportedStyles.styles,
    canvas: {
      // Styles inside the canvas and doesn't get exported
      frameStyle: `
      ${canvasOnlyStyles.styles}
      ${HLCollectionCardPlugin.getStyles(theme)}
      `,
    },

    storageManager: false,
    undoManager: { trackSelection: false },
    selectorManager: { componentFirst: true },
    showOffsets: true,
    showOffsetsSelected: true,
    forceClass: false,

    keepUnusedStyles: true,

    plugins: [
      CoreAdjustmentsPlugin(),
      CoreComponentsPlugin(theme),
      BaseHLComponentPlugin(theme),
      HLCollectionCardPlugin(theme),
      // coreComponentsPlugin.initializePlugin,
      // baseReactComponent,
      // mantineCOmponents,
    ],
    projectData,
  };

  const onEditor = (editor: Editor) => {
    console.log("Editor loaded", { editor });
    // @ts-ignore
    window.editor = editor;
    editorRef.current = editor;

    editor.on("load", () => {
      const wrapper = editor.getWrapper();
      if (wrapper != null) {
        console.log("🧃 wrapper", wrapper);
        wrapper.set("selectable", false);
        wrapper.set("hoverable", false);
        wrapper.set("badgable", false);
        wrapper.set("highlightable", false);
        wrapper.set("droppable", false);
        wrapper.set("stylable", []);
      }

      const body = editor.Canvas.getBody();
      body.addEventListener("paste", (e) => {
        if (e.target instanceof HTMLElement) {
          if (e.target.contentEditable === "true") {
            e.preventDefault();
            const text = e.clipboardData?.getData("text") || "";
            // @ts-ignore
            e.target.ownerDocument.execCommand("insertText", false, text);
          }
        }
      });

      // StyleManager.getBuiltIn("font-family")!.default = "Aeonik Pro";
      // StyleManager.getBuiltIn("font-family")!.options = []
      // editor.StyleManager.addBuiltIn("general", {
      //   type: "select",
      //   label: "Font Family",
      //   property: "font-family",
      //   default: "Aeonik Pro",
      //   // options: [{ id: "Aeonik Pro, sans-serif", label: "Aeonik Pro" }],
      // });
    });

    // editor.on("component:create", (component) => {
    //   console.log("component:create", component);
    // });

    // editor.on("component:add", (component) => {
    //   console.log("component:add", component);
    // });

    // editor.on("component:remove", (component) => {
    //   console.log("component:remove", component);
    // });

    // editor.on("component:update", (component) => {
    //   console.log("component:update", component);
    // });

    editor.on(`block:drag:stop`, (component, block) => {
      // if component exists, means the drop was successful
      if (component) {
        // component.addStyle({ color: "red" }); // eg. update the style
        // Do you need the parent who contains this new added component??
        // const parent = component.parent();
        // ...
        // console.log("block:drag:stop", { component, block, parent });
        console.log("block:drag:stop", { component, block });
        const blockDropStyles = block.get("attributes")?.blockDropStyles as {
          rules: {
            rule: string;
            atRuleType?: string;
            atRuleParams?: string;
            value: Record<string, unknown>;
          }[];
        };

        if (blockDropStyles != null) {
          const componentId = component.getId();
          const rules = blockDropStyles.rules;

          rules.forEach((ruleDefinition) => {
            const rule = ruleDefinition.rule.replaceAll(
              ".id",
              `#${componentId}`
            );

            editor.Css.setRule(rule, ruleDefinition.value, {
              atRuleType: ruleDefinition.atRuleType,
              atRuleParams: ruleDefinition.atRuleParams,
            });
          });
          console.log("blockDropStyles", { componentId, rules });
        }
      }
    });

    /*
    editor.Css.setRule('.class1:hover', { color: 'red' }, {
    atRuleType: 'media',
    atRuleParams: '(min-width: 500px)',
    });
    
*/

    editor.Commands.add("hl:preview", onShowPreview);
    editor.Commands.add("hl:save", (editor: Editor, options = {}) => {
      const css = editor.getCss() ?? "";
      const html = editor.getHtml() ?? "";
      const data = editor.getProjectData();
      console.log("hl:save", options, { css, html, data });
      onSave({ css, html, data });
    });
    editor.Commands.add("hl:toggle-helpers", (editor: Editor) => {
      editor.getWrapper()?.getEl()?.classList?.toggle("gjs-show-helpers");
    });

    editor.Commands.add("hl:export", () => {
      setShowExportModal(true);
    });

    editor.Commands.add("hl:import", () => {
      setShowImportModal(true);
    });

    // Hack so that the elements are available when the editor is loaded
    setTimeout(() => {
      editor.runCommand("core:component-outline");
      editor.runCommand("hl:toggle-helpers");
    }, 0);
  };

  return (
    <Box
      id="gjs-builder"
      sx={{
        height: "100%",
        width: "100%",
        maxHeight: "100%",
      }}
    >
      <GjsEditor grapesjs={grapesjs} onEditor={onEditor} options={gjsOptions}>
        <Stack
          spacing={0}
          w={"100%"}
          h={"100%"}
          mah={"100%"}
          sx={{ overflow: "hidden" }}
        >
          <WithEditor>
            <BuilderHeader />
          </WithEditor>

          <Group
            spacing={0}
            align="stretch"
            noWrap
            sx={{
              flexGrow: 1,
              minHeight: 0,
              background: "var(--mantine-color-gray-5)",
            }}
          >
            <BuilderLeftSidebar />
            <Canvas style={{ flexGrow: 1 }} />
            <BuilderRightSidebar />
          </Group>

          <Flex
            justify={"center"}
            align={"center"}
            sx={{
              height: 24,
              width: "100%",
              background: "var(--mantine-color-gray-1)",
              borderTop: "1px solid var(--mantine-color-divider-0)",
            }}
          >
            <Text size={"xs"} c={"dimmed"}>
              Highlight Builder v0.1-alpha
            </Text>
          </Flex>
        </Stack>

        <WithEditor>
          <Modal
            opened={showExportModal}
            onClose={() => {
              setShowExportModal(false);
            }}
            title="Export"
          >
            <ExportModal />
          </Modal>
          <Modal
            opened={showImportModal}
            onClose={() => {
              setShowImportModal(false);
            }}
            title="Import"
          >
            <ImportModal />
          </Modal>
        </WithEditor>

        <ModalProvider>
          {({ open, title, content, close }) => {
            console.log("Modal provider", content);
            return (
              <CustomModal
                size={"xl"}
                opened={open}
                title={title}
                onClose={close}
              >
                {content}
              </CustomModal>
            );
          }}
        </ModalProvider>

        <AssetsProvider>
          {(props) => (
            <props.Container>
              <CustomAssetManager {...props} />
            </props.Container>
          )}
        </AssetsProvider>
      </GjsEditor>
    </Box>
  );
};

const ExportModal = () => {
  const editor = useEditor();
  const data = editor.getProjectData();
  const { copied, copy } = useClipboard();

  return (
    <Box p={"sm"}>
      <Stack>
        <Textarea
          readOnly
          autosize
          rows={20}
          maxRows={20}
          defaultValue={JSON.stringify(data, null, 4)}
        />
        <Button
          onClick={() => {
            copy(JSON.stringify(data, null, 4));
          }}
        >
          {copied ? "Copied!" : "Copy to clipboard"}
        </Button>
      </Stack>
    </Box>
  );
};

const ImportModal = () => {
  const editor = useEditor();
  const [data, setData] = useState("");

  const pasteFromClipboard = async () => {
    const text = await navigator.clipboard.readText();
    console.log(text);
    setData(text);
  };

  return (
    <Box p={"sm"}>
      <Stack>
        <Textarea
          value={data}
          autosize
          rows={20}
          maxRows={20}
          onChange={(event) => {
            setData(event.target.value);
          }}
        />
        <Button onClick={pasteFromClipboard}>Paste from clipboard</Button>
        <Button
          onClick={() => {
            try {
              editor.loadProjectData(JSON.parse(data));
            } catch (error) {
              console.error(error);
            }
          }}
        >
          Import
        </Button>
      </Stack>
    </Box>
  );
};
